import { RadioGroup } from "devextreme-react/radio-group";
import { TextArea } from "devextreme-react/text-area";
import { ValidationSummary } from "devextreme-react/validation-summary";
import { Box, Item } from "devextreme-react/box";
import Button from "devextreme-react/button";
import Popup, { ToolbarItem } from "devextreme-react/popup";
import { useContext, useRef, useState } from "react";
import { useGetLookupValuesByType } from "../../../../api/lookup";
import { Field } from "../../../../layouts";
import { BusinessFlow, OrderStatus } from "../../../../types/enums";
import { useRequestCancellation } from "../api/request-cancellation";
import { RetailOrderContext } from "../context/RetailOrderContext";
import Validator, { RequiredRule } from "devextreme-react/validator";
import { showErrorMessage, showSuccessMessage } from "../../../../utils/messages";
import { LoadPopupPanel } from "../../../../components";
import { useAuth } from "../../../../contexts/auth";
import { useGetCompany } from "../../../../api/company";

export default function RequestCancellationButton() {
	const REFUND_REBILL_REASON = 900;
	const OTHER_REASON = 910;

	const { order, refetchOrder } = useContext(RetailOrderContext);

	const { user } = useAuth();
	const userCompany = useGetCompany(user?.companyId);

	const isLowes = userCompany.data?.businessFlowID === BusinessFlow.Lowes;

	const [isPopupVisible, setPopupVisibility] = useState(false);
	const [isTermsPopupVisible, setTermsPopupVisibility] = useState(false);
	const [selectedReason, setSelectedReason] = useState(order.details.cancellationReason ?? REFUND_REBILL_REASON);

	const cancellationReasons = useGetLookupValuesByType('CFIReasons', isPopupVisible);

	const commentRef = useRef<TextArea>(null);

	const requestCancellation = useRequestCancellation(
		() => {
			setPopupVisibility(false);
			setTermsPopupVisibility(false);
			showSuccessMessage('Cancellation request has been sent');
			refetchOrder();
		},
		(error) => showErrorMessage(error)
	)

	return (
		<>
			<Button elementAttr={{ 'data-testid': 'btn-request-ro-cancellation' }}
				text="RO Cancellation"
				icon="Inscyth-Cancel"
				visible={userCompany.data?.enableROCancellation &&
					order.details.orderStatusID !== OrderStatus.Cancelled &&
					order.details.orderStatusID !== OrderStatus.Closed &&
					!order.details.dealerCancellationReason}
				disabled={isPopupVisible}
				onClick={() => setPopupVisibility(true)}
			>
			</Button>
			<Popup
				title="Request Retail Order Cancellation"
				width={700}
				height={550}
				visible={isPopupVisible}
				hideOnOutsideClick={true}
				onHiding={() => setPopupVisibility(false)}
			>
				<ToolbarItem
					options={{
						icon: 'dx-icon dx-icon-close',
						onClick: function () {
							setPopupVisibility(false);
						}
					}}
					widget="dxButton"
					location="after"
				/>
				<ValidationSummary validationGroup="requestCancellationGroup"></ValidationSummary>
				<Box>
					<Item ratio={0} baseSize={250}>
						<RadioGroup
							dataSource={cancellationReasons.data}
							valueExpr={'lookupID'}
							displayExpr={'displayValue'}
							value={selectedReason}
							onValueChanged={(e) => {
								setSelectedReason(e.value);
								if (e.value !== OTHER_REASON)
									commentRef.current?.instance.option('value', '');
							}}
						/>
					</Item>
					<Item ratio={1}>
						<Field label={'Comment'}
							className="black-label"
							vertical={true}
							visible={selectedReason === OTHER_REASON}>
							<TextArea ref={commentRef} height={250}>
								<Validator validationGroup="requestCancellationGroup">
									<RequiredRule message={'Comment is required'} />
								</Validator>
							</TextArea>
						</Field>
					</Item>
				</Box>
				<ToolbarItem
					options={{
						type: 'normal',
						stylingMode: 'contained',
						text: 'Submit',
						validationGroup: 'requestCancellationGroup',
						onClick: (e: any) => {
							if (!e.validationGroup || e.validationGroup.validate().isValid) {

								if (selectedReason === REFUND_REBILL_REASON) {
									requestCancellation.mutate({
										retailOrderID: order.details.retailOrderID,
										acceptedReturnPolicy: false,
										cancellationReason: selectedReason,
										comment: commentRef.current?.instance.option("value")
									});
								}
								else {
									setTermsPopupVisibility(true);
								}
							}
						}
					}}
					toolbar={'bottom'}
					widget="dxButton"
					location="after"
				>
				</ToolbarItem>
				<ToolbarItem
					options={{
						type: 'normal',
						stylingMode: 'contained',
						text: 'Cancel',
						onClick: () => setPopupVisibility(false)
					}}
					toolbar={'bottom'}
					widget="dxButton"
					location="after"
				>
				</ToolbarItem>
				<LoadPopupPanel visible={cancellationReasons.isLoading || requestCancellation.isLoading}></LoadPopupPanel>
			</Popup>
			<Popup
				title={isLowes ? "allen + roth™ Countertop Return Policy" : "Sage Countertop Cancellation/Return Policy"}
				width={700}
				height={400}
				visible={isTermsPopupVisible}
				hideOnOutsideClick={true}
				onHiding={() => setTermsPopupVisibility(false)}
			>
				<ToolbarItem
					options={{
						icon: 'dx-icon dx-icon-close',
						onClick: function () {
							setTermsPopupVisibility(false);
						}
					}}
					widget="dxButton"
					location="after"
				/>
				<ol>
					<li>Contact your {isLowes ? <>allen + roth&trade;</> : "Sage"} CFI immediately for all returns/cancellations</li>
					<li>Countertop cancellation prior to template date - {isLowes ? "CFI" : "Sage"} receives 0%</li>
					<li>Template completed and then customer cancels order - {isLowes ? "CFI" : "Sage"} receives {isLowes ? "local store" : userCompany.data?.companyCode === "CliqStudios" ? "$150.00" : "$99.00"} detail fee</li>
					<li>{isLowes ? "CFI" : "Sage"} receives material, customer cancels order, but fabrication has not started - {isLowes ? "CFI" : "Sage"} receives 40% of P.O. cost</li>
					<li>Fabrication complete, customer cancels order, but top not installed - {isLowes ? "CFI" : "Sage"} receives 80% of P.O. cost</li>
					<li>No refunds on installed goods</li>
				</ol>
				<ToolbarItem
					options={{
						type: 'normal',
						stylingMode: 'contained',
						text: 'Accept Terms & Conditions',
						onClick: (e: any) => {
							requestCancellation.mutate({
								retailOrderID: order.details.retailOrderID,
								acceptedReturnPolicy: true,
								cancellationReason: selectedReason,
								comment: commentRef.current?.instance.option("value")
							});
						}
					}}
					toolbar={'bottom'}
					widget="dxButton"
					location="after"
				>
				</ToolbarItem>
				<ToolbarItem
					options={{
						type: 'normal',
						stylingMode: 'contained',
						text: 'No Thanks',
						onClick: () => setTermsPopupVisibility(false)
					}}
					toolbar={'bottom'}
					widget="dxButton"
					location="after"
				>
				</ToolbarItem>
			</Popup>
		</>
	)
}
